import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
  Snackbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import ClearIcon from '@material-ui/icons/Clear';
import MuiAlert from '@material-ui/lab/Alert';
import { generateEpicrisisDraft, transformEpicrisisToFormData } from './epicrisisUtils';
import { useFeature } from '../../../featureGating/featureGating';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: 'clamp(320px, 92vw, 960px)',
    minWidth: 'clamp(320px, 92vw, 600px)',
    borderRadius: 18,
    overflow: 'hidden',
  },
  dialogHeader: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
  },
  dialogTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  dialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  dialogContent: {
    backgroundColor: '#e6eaf0',
    padding: '16px !important',
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
  contentShell: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    border: '1px solid #dce8f5',
    overflow: 'hidden',
  },
  tabContent: {
    padding: theme.spacing(1),
    backgroundColor: '#e6eaf0',
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #dce8f5',
    padding: theme.spacing(0.5, 1, 0),
    '& .MuiTab-root.Mui-selected': {
      backgroundColor: '#f7faff',
      boxShadow: '0 2px 8px rgba(0, 45, 85, 0.1)',
    },
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  tabRoot: {
    color: '#002D55',
    fontWeight: 700,
    textTransform: 'none',
    minHeight: 44,
    borderRadius: '14px 14px 0 0',
  },
  statusReportContent: {
    backgroundColor: '#f7faff',
    padding: theme.spacing(2),
    borderRadius: 18,
    border: '1px solid #dce8f5',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionHeader: {
    fontWeight: 700,
    fontSize: 'clamp(0.76rem, 0.82vw, 1rem)',
    color: '#002D55',
    marginBottom: theme.spacing(1), 
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  textField: {
    marginBottom: theme.spacing(1),
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#ffffff',
      '&:hover fieldset': {
        borderColor: '#FF7F27',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FF7F27',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 12,
    },
    '& .MuiInputBase-input': {
      color: '#002D55',
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-outlined': {
      color: '#002D55',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
  },
  languageToggle: {
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      color: '#002D55',
      fontSize: 'clamp(0.68rem, 0.78vw, 0.84rem)',
      fontWeight: 600,
    },
  },
  switchRail: {
    '& .MuiSwitch-track': {
      backgroundColor: '#002D55 !important',
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#FF7F27',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#ffd7bd !important',
    },
  },
  previewBox: {
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: 14,
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    border: '1px solid #dce8f5',
    color: '#002D55',
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: '#64748B',
  },
  errorPanel: {
    marginBottom: theme.spacing(2),
    padding: '12px 16px',
    backgroundColor: '#fff4f2',
    border: '1px solid #f2c1bc',
    color: '#c62828',
    borderRadius: 14,
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default function ReportDialog({ 
  open, 
  onClose, 
  onSave, 
  initialData, 
  mode, 
  metrics, 
  reportStats,
  // New props for epicrisis generation
  selectedPatient,
  userToken,
  onEpicrisisGenerated,
  journalSections,
  appliedDiagnosis
}) {
  const classes = useStyles();
  const canGenerateEpicrisis = useFeature('medcom_epicrisis');
  const [tabValue, setTabValue] = useState(0);
  const [language, setLanguage] = useState('en');
  const [isEditing, setIsEditing] = useState(mode === 'create');
  const [formData, setFormData] = useState({
    // Status Report fields
    baseline_comparison: '',
    compliance_report: '',
    metrics_summary: '',
    physiotherapist_notes: '',
    // Epicrisis fields
    referral_reason: '',
    status: '',
    gp_suggestion: '',
    physiotherapy_diagnosis: '',
    treatment_summary: '',
    patient_education: '',
  });

  // State for epicrisis auto-generation
  const [generatingEpicrisis, setGeneratingEpicrisis] = useState(false);
  const [epicrisisError, setEpicrisisError] = useState(null);
  const [epicrisisGenerated, setEpicrisisGenerated] = useState(false);
  const [epicrisisSnackbarOpen, setEpicrisisSnackbarOpen] = useState(false);

  const translations = {
    en: {
      statusReport: 'Status Report',
      epicrisis: 'Epicrisis',
      baseline: 'Baseline vs Current Status',
      compliance: 'Compliance Report',
      metrics: 'Metrics Summary',
      statusSummary: 'Status summary',
      improvements: 'Calculated Improvements',
      notes: 'Physiotherapist Notes',
      referral: 'Referral Reason',
      status_text: 'Status',
      gp: 'GP Suggestion',
      diagnosis: 'Clinical Examination & Findings',
      treatmentSummary: 'Treatment Summary',
      education: 'Patient Education',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      english: 'English',
      danish: 'Dansk',
      preview: 'Preview Mode',
      editing: 'Editing Mode',
    },
    da: {
      statusReport: 'Statusrapport',
      epicrisis: 'Epikrise',
      baseline: 'Baseline vs nuværende status',
      compliance: 'Compliance-rapport',
      metrics: 'Metriksammenfattelse',
      statusSummary: 'Statussammenfatning',
      improvements: 'Beregnet forbedring',
      notes: 'Fysiotherapeutens noter',
      referral: 'Henvisningsårsag',
      status_text: 'Status',
      gp: 'Praktiserende læges forslag',
      diagnosis: 'Fysioterapeutisk diagnose',
      treatmentSummary: 'Behandlingssammenfattelse',
      education: 'Patientundervisning',
      save: 'Gem',
      cancel: 'Annuller',
      edit: 'Rediger',
      english: 'English',
      danish: 'Dansk',
      preview: 'Forhåndsvisning',
      editing: 'Redigering',
    },
    };

    const handleClearAllFields = () => {
      setFormData({
        // Status Report fields
        baseline_comparison: '',
        compliance_report: '',
        metrics_summary: '',
        physiotherapist_notes: '',
        // Epicrisis fields
        referral_reason: '',
        status: '',
        gp_suggestion: '',
        physiotherapy_diagnosis: '',
        treatment_summary: '',
        patient_education: '',
      });
      setEpicrisisGenerated(false);
      setEpicrisisSnackbarOpen(false);
    };

    const handleClearField = (field) => {
      setFormData(prev => ({
        ...prev,
        [field]: '',
      }));
  };

    const handleEpicrisisSnackbarClose = (event, reason) => {
      if (reason === 'clickaway') return;
      setEpicrisisSnackbarOpen(false);
    };

  const t = translations[language];

  const getPainFromBaseline = (baselinePayload) => {
    if (!baselinePayload || !baselinePayload.baselines) return null;
    const pain = baselinePayload.baselines.selfRatedPain;
    if (pain && Array.isArray(pain.items) && pain.items.length) {
      const it = pain.items[0];
      if (it && it.numeric_value !== undefined && it.numeric_value !== null) return Number(it.numeric_value);
      if (it && it.answerId) return Number.parseFloat(it.answerId);
    }
    return null;
  };

  const getPainFromImprovements = (improvements) => {
    if (!improvements || !Array.isArray(improvements.comparisons)) return null;
    const comp = improvements.comparisons.find((c) => {
      const id = (c.question_identifier || '').toLowerCase();
      if (id.includes('_atbest') || id.includes('_atworst')) return false;
      return id.includes('current') || id.includes('selfratedpain') || id.includes('pain');
    });
    if (!comp) return null;
    if (comp.followup_value !== undefined && comp.followup_value !== null) return Number(comp.followup_value);
    if (comp.baseline_value !== undefined && comp.baseline_value !== null) return Number(comp.baseline_value);
    return null;
  };

  const getFunctionFromImprovements = (improvements) => {
    if (!improvements || !Array.isArray(improvements.comparisons)) return null;
    const comp = improvements.comparisons.find((c) => {
      const id = (c.question_identifier || '').toLowerCase();
      if (id.includes('_atbest') || id.includes('_atworst')) return false;
      if (id.includes('selfratedfunction') || id.includes('function')) return true;
      // For PSFS, require additional context to distinguish from pain
      if (id.includes('psfs') && (id.includes('function') || id.includes('goal') || id.includes('main'))) return true;
      return false;
    });
    if (!comp) return null;
    return {
      baseline: comp.baseline_value !== undefined && comp.baseline_value !== null ? Number(comp.baseline_value) : null,
      latest: comp.followup_value !== undefined && comp.followup_value !== null ? Number(comp.followup_value) : null,
      baseline_text: comp.baseline_text || null,
      percent_change: comp.percent_change !== undefined ? comp.percent_change : null,
      absolute_change: comp.absolute_change !== undefined ? comp.absolute_change : null
    };
  };

  const getFunctionFromBaseline = (baselinePayload) => {
    if (!baselinePayload || !baselinePayload.baselines) return null;
    const func = baselinePayload.baselines.selfRatedFunction;
    if (!func || !Array.isArray(func.items) || func.items.length === 0) return null;
    const it = func.items[0];
    if (it && it.numeric_value !== undefined && it.numeric_value !== null) return Number(it.numeric_value);
    if (it && it.answerId) return Number.parseFloat(it.answerId);
    return null;
  };

  const getNpsFromImprovements = (improvements) => {
    if (!improvements || !Array.isArray(improvements.comparisons)) return null;
    const comp = improvements.comparisons.find((c) => {
      const id = (c.question_identifier || '').toLowerCase();
      return id.includes('nps') || id.includes('selfrated_nps') || id.includes('selfratednps') || id.includes('netpromoter');
    });
    if (!comp) return null;
    return {
      baseline: comp.baseline_value !== undefined && comp.baseline_value !== null ? Number(comp.baseline_value) : null,
      latest: comp.followup_value !== undefined && comp.followup_value !== null ? Number(comp.followup_value) : null,
    };
  };

  const getNpsFromBaseline = (baselinePayload) => {
    if (!baselinePayload || !baselinePayload.baselines) return null;
    const srFunc = baselinePayload.baselines.selfRatedFunction;
    if (srFunc && Array.isArray(srFunc.items) && srFunc.items.length) {
      const it = srFunc.items[0];
      const maybe = it && it.numeric_value !== undefined && it.numeric_value !== null
        ? Number(it.numeric_value)
        : (it && it.answerId ? Number.parseFloat(it.answerId) : null);
      if (maybe !== null && !Number.isNaN(maybe) && maybe >= 0 && maybe <= 10) return maybe;
    }
    return null;
  };

  const fmtNum = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (Number.isNaN(n)) return '-';
    const rounded = Math.round(n * 10) / 10;
    return String(rounded);
  };

  const fmtDelta = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (Number.isNaN(n)) return '-';
    const rounded = Math.round(n * 10) / 10;
    return `${rounded > 0 ? '+' : ''}${rounded}`;
  };

  const fmtPercent = (value) => {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    if (Number.isNaN(n)) return null;
    const rounded = Math.round(n * 10) / 10;
    return `${rounded}%`;
  };

  const calcPercentChange = (baseline, latest) => {
    if (baseline === null || baseline === undefined || latest === null || latest === undefined) return null;
    const b = Number(baseline);
    const l = Number(latest);
    if (Number.isNaN(b) || Number.isNaN(l) || b === 0) return null;
    return ((l - b) / b) * 100;
  };

  const calcImprovementPercent = (baseline, latest, invert = false) => {
    const pct = calcPercentChange(baseline, latest);
    if (pct === null) return null;
    return invert ? -pct : pct;
  };

  const findComparison = (improvements, predicate) => {
    if (!improvements || !Array.isArray(improvements.comparisons)) return null;
    return improvements.comparisons.find(predicate) || null;
  };

  const getComparisonByIdentifier = (improvements, matcher) => {
    return findComparison(improvements, (c) => {
      const id = (c.question_identifier || '').toLowerCase();
      return matcher(id);
    });
  };

  const getFunctionLabel = (baselinePayload, improvementsPayload) => {
    if (metrics && metrics.baseline_text) return String(metrics.baseline_text);
    const comp = getComparisonByIdentifier(improvementsPayload, (id) => id.includes('selfratedfunction') || id.includes('psfs') || id.includes('function'));
    if (comp && (comp.baseline_text || comp.followup_text)) return String(comp.baseline_text || comp.followup_text);
    const func = baselinePayload && baselinePayload.baselines && baselinePayload.baselines.selfRatedFunction;
    if (func && Array.isArray(func.items) && func.items.length) {
      return String(func.items[0].text_value || '') || null;
    }
    return null;
  };

  const formatTimeInTreatment = (days) => {
    if (!days || Number.isNaN(Number(days))) return null;
    const d = Math.max(1, Math.round(Number(days)));
    if (d < 7) return `${d} days`;
    const weeks = Math.round(d / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  };

  const computedImprovements = React.useMemo(() => {
    const baselinePayload = metrics && (metrics.baseline_payload || metrics.baseline);
    const improvementsPayload = metrics && (metrics.improvements_payload || metrics.improvements);
    if (!baselinePayload && !improvementsPayload) return null;

    const painBaseline = getPainFromBaseline(baselinePayload);
    const painLatest = getPainFromImprovements(improvementsPayload);

    const funcImp = getFunctionFromImprovements(improvementsPayload);
    const funcBaseline = funcImp && funcImp.baseline !== null ? funcImp.baseline : getFunctionFromBaseline(baselinePayload);
    const funcLatest = funcImp && funcImp.latest !== null ? funcImp.latest : null;

    const npsImp = getNpsFromImprovements(improvementsPayload);
    const npsBaseline = npsImp && npsImp.baseline !== null ? npsImp.baseline : getNpsFromBaseline(baselinePayload);
    const npsLatest = npsImp && npsImp.latest !== null ? npsImp.latest : null;

    const painChange = (painBaseline !== null && painLatest !== null) ? (painLatest - painBaseline) : null;
    const functionChange = (funcBaseline !== null && funcLatest !== null) ? (funcLatest - funcBaseline) : null;
    const npsChange = (npsBaseline !== null && npsLatest !== null) ? (npsLatest - npsBaseline) : null;

    return {
      pain_baseline: painBaseline,
      pain_latest: painLatest,
      pain_change: painChange,
      function_baseline: funcBaseline,
      function_latest: funcLatest,
      function_change: functionChange,
      nps_baseline: npsBaseline,
      nps_latest: npsLatest,
      nps_change: npsChange,
    };
  }, [metrics]);

  const buildStatusSummary = () => {
    console.log('[ReportDialog] buildStatusSummary called with reportStats:', reportStats);
    const baselinePayload = metrics && (metrics.baseline_payload || metrics.baseline);
    const improvementsPayload = metrics && (metrics.improvements_payload || metrics.improvements);
    const paragraphs = [];

    // Single opening paragraph: time, sessions, exercises, phase, compliance, and pain average
    const openingParts = [];
    const timeText = formatTimeInTreatment(reportStats && reportStats.timeInTreatmentDays);
    let startDateText = '';
    if (reportStats && reportStats.assessmentStartDate) {
      const d = new Date(reportStats.assessmentStartDate);
      if (!isNaN(d.getTime())) {
        startDateText = ` since ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
    }
    if (timeText) {
      openingParts.push(`The patient has been in treatment for ${timeText}${startDateText}`);
    }
    if (reportStats && typeof reportStats.totalSessions === 'number') {
      openingParts.push(`completing ${reportStats.totalSessions} session${reportStats.totalSessions !== 1 ? 's' : ''}`);
      if (typeof reportStats.last7Sessions === 'number') {
        openingParts.push(`(${reportStats.last7Sessions} in the last 7 days)`);
      }
    }
    if (reportStats && reportStats.currentExercisesCount) {
      openingParts.push(`with ${reportStats.currentExercisesCount} active exercise${reportStats.currentExercisesCount !== 1 ? 's' : ''}`);
    }
    if (reportStats && reportStats.phaseName) {
      let phaseText = `Currently in ${reportStats.phaseName}`;
      if (reportStats.phaseProgress !== null && reportStats.phaseProgress !== undefined) {
        phaseText += `. (${Math.round(reportStats.phaseProgress)}% complete)`;
      }
      openingParts.push(phaseText);
    }
    
    // Add compliance info to opening
    if (reportStats && reportStats.complianceOverall !== null && reportStats.plannedSessionsPerDay) {
      const compPct = fmtNum(reportStats.complianceOverall);
      const avgPerDay = fmtNum(reportStats.avgSessionsPerDay || 0);
      const planned = reportStats.plannedSessionsPerDay;
      openingParts.push(`The patient has completed ${compPct}% of prescribed sessions on average — about ${avgPerDay}/${planned} sessions per day`);
    }
    
    // Add average pain per exercise to opening
    if (reportStats && (reportStats.avgPainLast7 !== null || reportStats.avgPainOverall !== null)) {
      if (reportStats.avgPainLast7 !== null) {
        let painText = `On average, the patient reports ${fmtNum(reportStats.avgPainLast7)} pain per exercise over the last 7 days`;
        if (reportStats.avgPainOverall !== null) {
          painText += ` (overall average: ${fmtNum(reportStats.avgPainOverall)})`;
        }
        openingParts.push(painText);
      } else if (reportStats.avgPainOverall !== null) {
        openingParts.push(`On average, the patient reports ${fmtNum(reportStats.avgPainOverall)} pain per exercise`);
      }
    }
    
    if (openingParts.length) {
      paragraphs.push(openingParts.join('. '));
    }

    // Treatment effect section with bullet points
    const treatmentEffects = [];
    
    // Function bullets — emit one per distinct PSFS / SelfRatedFunction
    // main goal so multi-goal patients (e.g. "working at office" +
    // "carrying shopping bags") get a line for each instead of
    // collapsing to whichever the legacy single-pair extraction picked
    // first. Mirrors the multi-goal logic already used by PatientTable
    // and the backend epicrisis format_status.
    const functionComps = (improvementsPayload && Array.isArray(improvementsPayload.comparisons))
      ? improvementsPayload.comparisons.filter(c => {
          const id = (c.question_identifier || '').toLowerCase();
          if (id.includes('_atbest') || id.includes('_atworst')) return false;
          const hasBaseline = c.baseline_value !== undefined && c.baseline_value !== null;
          const hasFollowup = c.followup_value !== undefined && c.followup_value !== null;
          if (!hasBaseline && !hasFollowup) return false;
          if (id.includes('selfratedfunction') || id.includes('function')) return true;
          if (id.includes('psfs') && (id.includes('function') || id.includes('goal') || id.includes('main'))) return true;
          return false;
        })
      : [];

    // Deduplicate by question_identifier so backend duplicates don't
    // double-render.
    const seenFnIds = new Set();
    const uniqueFunctionComps = [];
    for (const c of functionComps) {
      const key = (c.question_identifier || '').toLowerCase();
      if (seenFnIds.has(key)) continue;
      seenFnIds.add(key);
      uniqueFunctionComps.push(c);
    }

    if (uniqueFunctionComps.length > 0) {
      uniqueFunctionComps.forEach(comp => {
        const baseline = comp.baseline_value;
        const latest = (comp.followup_value !== null && comp.followup_value !== undefined)
          ? comp.followup_value
          : baseline;
        const label = comp.baseline_text || comp.followup_text || 'their primary activity';
        const pct = (baseline !== null && baseline !== undefined && latest !== null && latest !== undefined)
          ? calcImprovementPercent(baseline, latest, false)
          : null;

        let funcText = `The patient has scored their ability to do "${label}" at ${fmtNum(latest)}/100`;
        if (pct !== null) {
          const direction = pct > 0 ? 'improved' : pct < 0 ? 'declined' : 'remained unchanged';
          if (pct === 0) {
            funcText += ` — this has remained unchanged since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
          } else {
            funcText += ` — this has ${direction} by ${fmtNum(Math.abs(pct))}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
          }
        } else if (baseline !== null && baseline !== undefined) {
          funcText += ` (baseline was ${fmtNum(baseline)})`;
        }
        funcText += '.';
        treatmentEffects.push(funcText);
      });
    } else if (computedImprovements && computedImprovements.function_baseline !== null && computedImprovements.function_latest !== null) {
      // Fallback: legacy single-pair path when improvements payload has
      // no comparisons array (e.g. very old cached data).
      const label = getFunctionLabel(baselinePayload, improvementsPayload);
      const baseline = computedImprovements.function_baseline;
      const latest = computedImprovements.function_latest;
      const pct = calcImprovementPercent(baseline, latest, false);

      let funcText = `The patient has scored their ability to do "${label || 'their primary activity'}" at ${fmtNum(latest)}/100`;
      if (pct !== null) {
        const direction = pct > 0 ? 'improved' : 'declined';
        funcText += ` — this has ${direction} by ${fmtNum(Math.abs(pct))}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
      } else {
        funcText += ` (baseline was ${fmtNum(baseline)})`;
      }
      funcText += '.';
      treatmentEffects.push(funcText);
    }

    // Pain at best bullet
    const painBestComp = getComparisonByIdentifier(improvementsPayload, (id) => id.includes('atbest'));
    if (painBestComp && painBestComp.baseline_value !== undefined && painBestComp.followup_value !== undefined) {
      const baseline = painBestComp.baseline_value;
      const latest = painBestComp.followup_value;
      const pct = calcImprovementPercent(baseline, latest, true);
      
      let painBestText = `The patient reported their pain at best as ${fmtNum(latest)}/100`;
      if (pct !== null && pct > 0) {
        painBestText += ` — this has reduced by ${fmtNum(pct)}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
      } else if (pct !== null && pct < 0) {
        painBestText += ` — this has increased by ${fmtNum(Math.abs(pct))}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
      } else {
        painBestText += ` (baseline was ${fmtNum(baseline)})`;
      }
      painBestText += '.';
      treatmentEffects.push(painBestText);
    }

    // Pain at worst bullet
    const painWorstComp = getComparisonByIdentifier(improvementsPayload, (id) => id.includes('atworst'));
    if (painWorstComp && painWorstComp.baseline_value !== undefined && painWorstComp.followup_value !== undefined) {
      const baseline = painWorstComp.baseline_value;
      const latest = painWorstComp.followup_value;
      const pct = calcImprovementPercent(baseline, latest, true);
      
      let painWorstText = `At its worst, the patient's pain is now ${fmtNum(latest)}/100`;
      if (pct !== null && pct > 0) {
        painWorstText += ` — this has reduced by ${fmtNum(pct)}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
      } else if (pct !== null && pct < 0) {
        painWorstText += ` — this has increased by ${fmtNum(Math.abs(pct))}% since the start (from ${fmtNum(baseline)} to ${fmtNum(latest)})`;
      } else {
        painWorstText += ` (baseline was ${fmtNum(baseline)})`;
      }
      painWorstText += '.';
      treatmentEffects.push(painWorstText);
    }

    // Symptom frequency bullet
    const freqComp = getComparisonByIdentifier(improvementsPayload, (id) => id.includes('frequency'));
    if (freqComp && freqComp.baseline_value !== undefined && freqComp.followup_value !== undefined) {
      // Convert from weekly days to monthly normalized (days/week × 31/7)
      const baseline = freqComp.baseline_value * (31 / 7);
      const latest = freqComp.followup_value * (31 / 7);
      const pct = calcImprovementPercent(baseline, latest, true);
      
      let freqText = `Symptom frequency is currently ${fmtNum(latest)}/31 days`;
      if (pct !== null && pct > 0) {
        freqText += ` — this has reduced by ${fmtNum(pct)}% since the start (from ${fmtNum(baseline)}/31 to ${fmtNum(latest)}/31 days)`;
      } else if (pct !== null && pct < 0) {
        freqText += ` — this has increased by ${fmtNum(Math.abs(pct))}% since the start (from ${fmtNum(baseline)}/31 to ${fmtNum(latest)}/31 days)`;
      } else {
        freqText += ` (baseline was ${fmtNum(baseline)}/31 days)`;
      }
      freqText += '.';
      treatmentEffects.push(freqText);
    }
    
    // Add Treatment effect section if we have any effects
    if (treatmentEffects.length > 0) {
      paragraphs.push('Treatment effect:\n\n- ' + treatmentEffects.join('\n\n- '));
    }

    // Closing paragraph
    paragraphs.push("There has been no unexpected change or development in symptoms.");

    const finalSummary = paragraphs.join('\n\n');
    console.log('[ReportDialog] buildStatusSummary paragraphs:', paragraphs);
    console.log('[ReportDialog] buildStatusSummary returning:', finalSummary);
    return finalSummary;
  };

  // Initialize form data from initialData
  useEffect(() => {
    if (initialData) {
      setFormData({
        baseline_comparison: initialData.baseline_comparison || '',
        compliance_report: initialData.compliance_report || '',
        metrics_summary: initialData.metrics_summary || '',
        physiotherapist_notes: initialData.physiotherapist_notes || '',
        referral_reason: initialData.referral_reason || '',
        status: initialData.status || '',
        gp_suggestion: initialData.gp_suggestion || '',
        physiotherapy_diagnosis: initialData.physiotherapy_diagnosis || '',
        treatment_summary: initialData.treatment_summary || '',
        patient_education: initialData.patient_education || '',
      });
      setLanguage(initialData.language || 'en');
    }
  }, [initialData, open]);

  useEffect(() => {
    if (!open || initialData) return;
    // Wait for reportStats to be available before auto-filling
    if (!reportStats) return;
    const summary = buildStatusSummary();
    if (!summary) return;
    setFormData((prev) => {
      if (prev.metrics_summary && prev.metrics_summary.trim()) return prev;
      return {
        ...prev,
        metrics_summary: summary,
      };
    });
  }, [open, initialData, computedImprovements, reportStats, metrics]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle auto-generating epicrisis from backend
  const handleGenerateEpicrisis = async () => {
    if (!canGenerateEpicrisis) {
      setEpicrisisError('Epicrisis generation requires the Stage 2 Full Platform subscription.');
      return;
    }

    try {
      if (!selectedPatient || !userToken) {
        setEpicrisisError('Missing patient or authentication information');
        return;
      }

      const patientId = selectedPatient.user_id || selectedPatient.id || (selectedPatient.user && selectedPatient.user.id);
      const treatmentId = selectedPatient.treatment_id;

      if (!patientId || !treatmentId) {
        setEpicrisisError('Missing patient ID or treatment ID');
        return;
      }

      setGeneratingEpicrisis(true);
      setEpicrisisError(null);

      // Call backend to generate epicrisis, passing journal data if available
      const response = await generateEpicrisisDraft(
        treatmentId, 
        patientId, 
        userToken, 
        language,
        journalSections,
        appliedDiagnosis
      );

      // Transform the response to form data
      const transformedData = transformEpicrisisToFormData(response);

      // Merge with existing form data (generated data takes precedence)
      setFormData(prev => ({
        ...prev,
        referral_reason: transformedData.referral_reason,
        status: transformedData.status,
        gp_suggestion: transformedData.gp_suggestion,
        physiotherapy_diagnosis: transformedData.physiotherapy_diagnosis,
        treatment_summary: transformedData.treatment_summary,
        patient_education: transformedData.patient_education,
      }));

      setEpicrisisGenerated(true);
      setEpicrisisSnackbarOpen(true);
      setIsEditing(true); // Switch to editing mode to allow review

      // Notify parent component if callback provided
      if (onEpicrisisGenerated) {
        onEpicrisisGenerated(response);
      }

      // Auto-switch to Epicrisis tab
      setTabValue(0);
    } catch (error) {
      console.error('Error generating epicrisis:', error);
      setEpicrisisError(error.message || 'Failed to generate epicrisis. Please try again.');
      setGeneratingEpicrisis(false);
    } finally {
      setGeneratingEpicrisis(false);
    }
  };

  const handleSave = () => {
    // Determine which report type we're saving based on current tab
    const reportType = tabValue === 0 ? 'epicrisis' : 'status_report';
    
    onSave({
      ...formData,
      language,
      reportType,
      // Include patient and treatment info for backend save
      patient_id: selectedPatient?.user_id || selectedPatient?.id,
      treatment_id: selectedPatient?.treatment_id,
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderStatusReportTab = () => (
    <Box className={classes.tabContent}>
      <Box className={classes.statusReportContent}>
      {isEditing ? (
        <>
          <Box className={classes.section}>
            <Typography className={classes.sectionHeader}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <span>{t.statusSummary}</span>
                <Tooltip title="Clear this field">
                  <IconButton
                    size="small"
                    onClick={() => handleClearField('metrics_summary')}
                    style={{ color: '#FF7F27', marginLeft: 8 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Typography>
            <TextField
              className={classes.textField}
              multiline
              rows={18}
              value={formData.metrics_summary}
              onChange={(e) => handleFieldChange('metrics_summary', e.target.value)}
              variant="outlined"
              placeholder={t.statusSummary}
            />
          </Box>
        </>
      ) : (
        <>
          <Box className={classes.section}>
            <Typography className={classes.sectionHeader}>
              {t.statusSummary}
            </Typography>
            <Box className={classes.previewBox}>
              {formData.metrics_summary}
            </Box>
          </Box>
        </>
      )}
      </Box>
    </Box>
  );

  const renderEpicrisisTab = () => (
    <Box className={classes.tabContent}>
      {/* Generate button for create mode */}
      {mode === 'create' && !epicrisisGenerated && (
        <Box className={classes.section} style={{ textAlign: 'center', marginBottom: 24 }}>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleGenerateEpicrisis}
            disabled={generatingEpicrisis || !selectedPatient || !canGenerateEpicrisis}
            className={classes.confirmButton}
            style={{ padding: '10px 24px', fontSize: '1rem' }}
          >
            {generatingEpicrisis ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                Generating Epicrisis...
              </>
            ) : (
              'Generate Epicrisis'
            )}
          </Button>
          <Typography variant="body2" style={{ marginTop: 8, color: '#666' }}>
            {canGenerateEpicrisis
              ? 'Click to auto-generate an epicrisis draft from treatment data'
              : 'Epicrisis generation requires the Stage 2 Full Platform subscription'}
          </Typography>
        </Box>
      )}

      {/* Error alert */}
      {epicrisisError && (
        <Paper 
          elevation={0}
          style={{ 
            marginBottom: 16, 
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            color: '#c62828'
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            ⚠ {epicrisisError}
          </Typography>
        </Paper>
      )}

      <Box className={classes.statusReportContent}>
        {isEditing ? (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.referral}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('referral_reason')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={8}
                value={formData.referral_reason}
                onChange={(e) => handleFieldChange('referral_reason', e.target.value)}
                variant="outlined"
                placeholder={t.referral}
              />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.status_text}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('status')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={8}
                value={formData.status}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                variant="outlined"
                placeholder={t.status_text}
              />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.gp}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('gp_suggestion')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={8}
                value={formData.gp_suggestion}
                onChange={(e) => handleFieldChange('gp_suggestion', e.target.value)}
                variant="outlined"
                placeholder={t.gp}
              />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.diagnosis}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('physiotherapy_diagnosis')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={8}
                value={formData.physiotherapy_diagnosis}
                onChange={(e) => handleFieldChange('physiotherapy_diagnosis', e.target.value)}
                variant="outlined"
                placeholder={t.diagnosis}
              />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.treatmentSummary}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('treatment_summary')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={8}
                value={formData.treatment_summary}
                onChange={(e) => handleFieldChange('treatment_summary', e.target.value)}
                variant="outlined"
                placeholder={t.treatmentSummary}
              />
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <span>{t.education}</span>
                  <Tooltip title="Clear this field">
                    <IconButton
                      size="small"
                      onClick={() => handleClearField('patient_education')}
                      style={{ color: '#FF7F27', marginLeft: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              <TextField
                className={classes.textField}
                multiline
                minRows={3}
                maxRows={10}
                value={formData.patient_education}
                onChange={(e) => handleFieldChange('patient_education', e.target.value)}
                variant="outlined"
                placeholder={t.education}
              />
            </Box>
          </>
        ) : (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.referral}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.referral_reason}
              </Box>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.status_text}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.status}
              </Box>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.gp}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.gp_suggestion}
              </Box>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.diagnosis}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.physiotherapy_diagnosis}
              </Box>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.treatmentSummary}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.treatment_summary}
              </Box>
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t.education}
              </Typography>
              <Box className={classes.previewBox}>
                {formData.patient_education}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogHeader}>
        <Box className={classes.dialogTitleRow}>
          <span className={classes.dialogTitleText}>
            {tabValue === 1 ? t.statusReport : t.epicrisis}
          </span>
          <Box className={classes.languageToggle}>
            <FormControlLabel
              control={
                <Switch
                  checked={language === 'da'}
                  onChange={() => setLanguage(language === 'en' ? 'da' : 'en')}
                  className={classes.switchRail}
                />
              }
              label={language === 'en' ? t.english : t.danish}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.contentShell}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          className={classes.tabsContainer}
          TabIndicatorProps={{ className: classes.tabsIndicator }}
        >
          <Tab label={t.epicrisis} className={classes.tabRoot} />
          <Tab label={t.statusReport} className={classes.tabRoot} />
        </Tabs>

        {tabValue === 0 && renderEpicrisisTab()}
        {tabValue === 1 && renderStatusReportTab()}
        </Box>
      </DialogContent>

      <Snackbar
        open={epicrisisSnackbarOpen}
        autoHideDuration={4000}
        onClose={handleEpicrisisSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Alert onClose={handleEpicrisisSnackbarClose} severity="success">
          ✓ Epicrisis generated successfully. You can now review and edit the content below.
        </Alert>
      </Snackbar>

      <DialogActions className={classes.dialogActions}>
        <Box className={classes.actionRow}>
          <Tooltip title="Clear all fields">
            <Button
              onClick={handleClearAllFields}
              variant="contained"
              startIcon={<ClearIcon />}
              className={classes.cancelButton}
            >
              Clear all
            </Button>
          </Tooltip>
          <Box className={classes.actionGroup}>
            <Button
              onClick={onClose}
              variant="contained"
              className={classes.cancelButton}
            >
              {t.cancel}
            </Button>
            {mode === 'view' && (
              <Button
                onClick={() => setIsEditing(!isEditing)}
                variant="contained"
                className={classes.cancelButton}
              >
                {isEditing ? t.preview : t.edit}
              </Button>
            )}
            {(mode === 'create' || isEditing) && (
              <Button
                onClick={handleSave}
                variant="contained"
                className={classes.confirmButton}
              >
                {t.save}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
